/**
 * Custom Fields Type System
 *
 * This module provides a type-safe way to work with custom fields using discriminated unions.
 * Each field type has its own interface with specific config and default value types.
 *
 * Example usage with type narrowing:
 *
 * ```typescript
 * function processCustomField(field: CustomFieldEntry) {
 *   switch (field.type) {
 *     case ECustomFieldComponentType.SINGLE_SELECT:
 *       // TypeScript now knows field.config is IConfigForSingleSelect
 *       // and field.default is IDefaultForSingleSelect
 *       console.log(field.config.allowEmpty); // ✅ Type-safe
 *       console.log(field.default.value); // ✅ Type-safe
 *       break;
 *     case ECustomFieldComponentType.NUMBER:
 *       // TypeScript now knows field.config is ICustomFieldConfig
 *       // and field.default is IDefaultForNumberField
 *       console.log(field.config.unit); // ✅ Type-safe
 *       console.log(field.default.value); // ✅ Type-safe
 *       break;
 *   }
 * }
 * ```
 */

export interface IDefaultForSingleSelect {
  value: string;
}
export interface IDefaultForMultiSelect {
  values: string[];
}

export interface IDefaultForNumberField {
  value: number;
}

export type DefaultForOtherField = Record<string, unknown>;

export interface ICustomFieldConfig {
  values: string[];
  unit?: string;
}

export interface IConfigForSingleSelect {
  values: string[];
  allowEmpty: boolean;
}

export enum ECustomFieldComponentType {
  TEXT = 'text',
  TEXT_AREA = 'text_area',
  NUMBER = 'number',
  CHECKBOX = 'checkbox',
  SINGLE_SELECT = 'single_select',
  MULTI_SELECT = 'multi_select',
  FILE = 'file',
  IMAGE = 'image',
  DATE = 'date'
}

// Base interface with common properties
interface ICustomFieldBase {
  id: string;
  companyId: number;
  name: string;
}

// Specific interfaces for each field type
export interface ICustomTextField extends ICustomFieldBase {
  type: ECustomFieldComponentType.TEXT | ECustomFieldComponentType.TEXT_AREA;
  config: ICustomFieldConfig;
  default: DefaultForOtherField;
}

export interface ICustomNumberField extends ICustomFieldBase {
  type: ECustomFieldComponentType.NUMBER;
  config: ICustomFieldConfig;
  default: IDefaultForNumberField;
}

export interface ICustomCheckboxField extends ICustomFieldBase {
  type: ECustomFieldComponentType.CHECKBOX;
  config: ICustomFieldConfig;
  default: DefaultForOtherField;
}

export interface ICustomSingleSelectField extends ICustomFieldBase {
  type: ECustomFieldComponentType.SINGLE_SELECT;
  config: IConfigForSingleSelect;
  default: IDefaultForSingleSelect;
}

export interface ICustomMultiSelectField extends ICustomFieldBase {
  type: ECustomFieldComponentType.MULTI_SELECT;
  config: ICustomFieldConfig;
  default: IDefaultForMultiSelect;
}

export interface ICustomFileField extends ICustomFieldBase {
  type: ECustomFieldComponentType.FILE;
  config: ICustomFieldConfig;
  default: DefaultForOtherField;
}

export interface ICustomImageField extends ICustomFieldBase {
  type: ECustomFieldComponentType.IMAGE;
  config: ICustomFieldConfig;
  default: DefaultForOtherField;
}

export interface ICustomDateField extends ICustomFieldBase {
  type: ECustomFieldComponentType.DATE;
  config: ICustomFieldConfig;
  default: DefaultForOtherField;
}

// Union type for all custom field entries
export type CustomFieldEntry =
  | ICustomTextField
  | ICustomNumberField
  | ICustomCheckboxField
  | ICustomSingleSelectField
  | ICustomMultiSelectField
  | ICustomFileField
  | ICustomImageField
  | ICustomDateField;

/** @deprecated Use CustomFieldEntry instead for better type inference */
export type ICustomFieldEntry = CustomFieldEntry;

export interface IEntityCustomFields {
  [key: string]: CustomFieldEntry;
}

export enum ECustomFieldEntity {
  PRODUCTION_ADVANCEMENTS = 'production_advancements',
  PRODUCTION_BATCHES = 'production_batches',
  WORK_ORDERS = 'work_orders'
}

// TODO: remove these types (they should be defined in React Query hooks)

export interface CreateCustomFieldParams {
  name: string;
  type: ECustomFieldComponentType;
  entity: ECustomFieldEntity;
  config: any;
  default: any;
}

export interface UpdateCustomFieldParams {
  id: string;
  name: string;
  config?: any;
  default?: any;
}

export interface GetCustomFieldsParams {
  entity: ECustomFieldEntity;
}
